import React, { useState } from "react";
import styled from "styled-components";
import { Printer, X, History, Calendar } from "lucide-react";
import Layout from "./layout";
import TemplateDetailModal from "../Modal/TemplateDetailModal";
import EditTemplateModal from "../Modal/editTamplateModal";
import HealthResultModal from "../Modal/HealthResultModal";
import SendHealthResultModal from "../Modal/SendHealthResultModal";
import HealthReportModal from "../Modal/HealthReportModal";
import { useNavigate } from "react-router-dom";
import { templateData, healthRecordsData } from "../data/mockData";

const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 1.5fr 1.5fr 1.5fr auto;
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const GridItem = styled.div`
  grid-column: ${props => props.column};
  grid-row: ${props => props.row};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const DateInputContainer = styled.div`
  position: relative;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  height: 44px;
  white-space: nowrap;

  &:hover {
    background-color: #0891B2;
  }
`;

const PrintButton = styled.button`
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const TemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00B5B8;
  color: #00B5B8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TemplateBadgeText = styled.span`
  margin-right: 0.5rem;
`;

const RemoveButton = styled.button`
  background: white;
  border: 1px solid #00B5B8;
  cursor: pointer;
  color: #00B5B8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const ViewDataButton = styled.button`
  background-color: #FBFFFF;
  color: #9c9e9eff;
  border: 1px solid #CFD3D4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  text-decoration: underline;
  background: none;
  border: none;
  color: ${props => 
    props.status === 'รอการอนุมัติ' ? '#FF0404' :
    props.status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' ? '#00B5B8' :
    props.status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง' ? '#FF0404' : '#6F6F6F'
  };
`;

const ActionPrintButton = styled.button`
  background: none;
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  }
`;

const EditButton = styled.button`
  background-color: ${props => props.disabled ? '#F3F4F6' : '#FFFFFF'};
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: 1px solid ${props => props.disabled ? '#E5E7EB' : '#CFD3D4'};
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin: 0.125rem;

  &:hover {
    background-color: ${props => props.disabled ? '#F3F4F6' : '#F9FAFB'};
  }
`;

const SendResultButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: 1px solid #00B5B8;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #0891B2;
    border-color: #0891B2;
  }
`;

const HistoryButton = styled.button`
  background: none;
  border: none;
  color: #6F6F6F;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;

  &:hover {
    color: #6F6F6F;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6F6F6F;
  text-align: center;
  vertical-align: top;
`;

const HealthBook = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHealthResultModalOpen, setIsHealthResultModalOpen] = useState(false);
  const [isSendHealthResultModalOpen, setIsSendHealthResultModalOpen] = useState(false);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();


  const handleSearch = () => {
    console.log('Searching with:', {
      startDate,
      endDate,
      searchText,
      company,
      status
    });
  };

  const handlePrint = (recordId) => {
    console.log('Print clicked for record:', recordId);
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleViewData = (recordId) => {
    console.log('View data for record:', recordId);
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleRemoveTemplate = (recordId, templateIndex) => {
    const updatedRecords = healthRecords.map(record => {
      if (record.id === recordId) {
        const updatedTemplates = record.templates.filter((_, index) => index !== templateIndex);
        return { ...record, templates: updatedTemplates };
      }
      return record;
    });
    setHealthRecords(updatedRecords);
  };

  const handleEdit = (recordId) => {
    console.log('Edit record:', recordId);
    console.log('Setting isEditModalOpen to true');
    setIsEditModalOpen(true);
  };

  const handleSendResult = (recordId) => {
    console.log('Send result for record:', recordId);
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      if (record.isApproved) {
        // ถ้าเป็นการส่งใหม่ แสดง SendHealthResultModal
        setIsSendHealthResultModalOpen(true);
      } else {
        // ถ้าเป็นการส่งครั้งแรก แสดง HealthResultModal
        setIsHealthResultModalOpen(true);
      }
    }
  };

  const handleTemplateClick = (templateName) => {
    const template = templateData[templateName];
    if (template) {
      setSelectedTemplate(template);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleCloseHealthResultModal = () => {
    setIsHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApprove = (recordId) => {
    setHealthRecords(prev =>
      prev.map(record =>
        record.id === recordId
          ? { ...record, status: 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว', isApproved: true }
          : record
      )
    );
  };

  const handleViewHistory = (recordId) => {
    console.log('View history for record:', recordId);
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      navigate('/health-records-history', { state: { selectedRecord: record } });
    }
  };

  const handleCloseSendHealthResultModal = () => {
    setIsSendHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmSendAgain = () => {
    // อัพเดทสถานะเป็น "มีการแก้ไขรอการอนุมัติอีกครั้ง" และ isApproved = false
    if (selectedRecord) {
      setHealthRecords(prev =>
        prev.map(record =>
          record.id === selectedRecord.id
            ? {
                ...record,
                status: 'มีการแก้ไขรอการอนุมัติอีกครั้ง',
                isApproved: false
              }
            : record
        )
      );
    }
    setIsSendHealthResultModalOpen(false);
    setIsHealthResultModalOpen(true);
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedRecord(null);
  };


  const [healthRecords, setHealthRecords] = useState(healthRecordsData);

  return (
    <Layout>
      <PageContainer>
        <PageTitle>สมุดสุขภาพ</PageTitle>

        <FilterSection>
          {/* แถวที่ 1 - Labels */}
          <GridItem column="1" row="1">
            <Label>วันที่ตรวจเริ่ม</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ถึง</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>ค้นหาชื่อ-สกุล, เลขบัตร</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>บริษัท</Label>
          </GridItem>
          <GridItem column="5" row="1">
            <Label>สถานะ</Label>
          </GridItem>

          {/* แถวที่ 2 - Input Fields และ Buttons */}
          <GridItem column="1" row="2">
            <DateInputContainer>
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="2" row="2">
            <DateInputContainer>
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="3" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อค้นหา"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </GridItem>

          <GridItem column="4" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อบริษัท"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </GridItem>

          <GridItem column="5" row="2">
            <SelectInput
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">เลือกสถานะ</option>
              <option value="รอการอนุมัติ">รอการอนุมัติ</option>
              <option value="อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว">อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว</option>
              <option value="มีการแก้ไขรอการอนุมัติอีกครั้ง">มีการแก้ไขรอการอนุมัติอีกครั้ง</option>
            </SelectInput>
          </GridItem>

          <GridItem column="6" row="2">
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <SearchButton onClick={handleSearch}>
                ค้นหา
              </SearchButton>
              <PrintButton onClick={handlePrint}>
                <Printer size={16} />
                Print
              </PrintButton>
            </div>
          </GridItem>
        </FilterSection>

        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell rowSpan="2">ที่</TableHeaderCell>
                <TableHeaderCell rowSpan="2">วันที่ตรวจ</TableHeaderCell>
                <TableHeaderCell rowSpan="2">ชื่อผู้รับการตรวจ/เพศ/อายุ/โรคประจำตัว</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '120px' }}>HN</TableHeaderCell>
                <TableHeaderCell colSpan="3">ผลการตรวจสุขภาพ</TableHeaderCell>
                <TableHeaderCell rowSpan="2">การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น/ Doctor Note</TableHeaderCell>
                <TableHeaderCell rowSpan="2">การจัดการข้อมูล</TableHeaderCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell2 style={{ width: '80px' }}>พารามิเตอร์</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '80px' }}>ค่าที่ตรวจวัดได้</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '80px' }}>ค่าปกติ</TableHeaderCell2>
              </TableRow>
            </TableHeader>
            <tbody>
              {healthRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.no}</TableCell>
                  <TableCell>{record.date}</TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    <div><span style={{ fontWeight: '700' }}>ชื่อ:</span> <span style={{ fontWeight: '400' }}>{record.patientName}</span></div>
                    <div><span style={{ fontWeight: '700' }}>เพศ:</span> {record.gender}</div>
                    <div><span style={{ fontWeight: '700' }}>อายุ:</span> {record.age} ปี</div>
                    <div><span style={{ fontWeight: '700' }}>โรคประจำตัว:</span></div>
                    <div style={{ fontWeight: '400' }}>{record.diseases}</div>
                  </TableCell>
                  <TableCell>{record.hn}</TableCell>
                  <TableCell>
                    {record.parameters.map((param, index) => (
                      <div key={index} style={{ textAlign: 'left', marginBottom: '0.25rem' }}>
                        {param.name}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    {record.parameters.map((param, index) => (
                      <div key={index} style={{ marginBottom: '0.25rem' }}>
                        {param.result}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    {record.parameters.map((param, index) => (
                      <div key={index} style={{ marginBottom: '0.25rem' }}>
                        {param.normal}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                      {record.templates.map((template, index) => (
                        <TemplateBadge
                          key={index}
                          onClick={() => handleTemplateClick(template)}
                        >
                          <TemplateBadgeText>{template}</TemplateBadgeText>
                        </TemplateBadge>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {record.isApproved && (
                        <HistoryButton onClick={() => handleViewHistory(record.id)}>
                          <History />
                          ประวัติการส่งผลสุขภาพ
                        </HistoryButton>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                      <ViewDataButton onClick={() => handleViewData(record.id)}>
                        ดูข้อมูล
                      </ViewDataButton>
                      <StatusText>สถานะจัดส่ง</StatusText>
                      <StatusBadge status={record.status}>
                        {record.status}
                      </StatusBadge>
                      <ActionPrintButton
                        onClick={() => handlePrint(record.id)}
                        disabled={!record.isApproved}
                      >
                        <Printer size={14} />
                        Print
                      </ActionPrintButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      <TemplateDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateData={selectedTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
      />

      <HealthResultModal
        isOpen={isHealthResultModalOpen}
        onClose={handleCloseHealthResultModal}
        selectedRecord={selectedRecord}
        onApprove={handleApprove}
      />

      <SendHealthResultModal
        isOpen={isSendHealthResultModalOpen}
        onClose={handleCloseSendHealthResultModal}
        onConfirm={handleConfirmSendAgain}
      />

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedRecord}
      />
    </Layout>
  );
};

export default HealthBook;